import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Play, Clock, Star, Filter, BookOpen } from "lucide-react";
import { useState } from "react";

const categories = ["Tous", "Débutant", "Intermédiaire", "Avancé"];

const tutorials = [
  {
    title: "Créer votre première image avec Floop",
    description: "Un guide complet pour les débutants qui souhaitent générer leur première image.",
    thumbnail: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&h=340&fit=crop",
    duration: "5 min",
    level: "Débutant",
    views: "12.5k",
  },
  {
    title: "Maîtriser l'art des prompts",
    description: "Apprenez à écrire des prompts efficaces pour obtenir exactement ce que vous voulez.",
    thumbnail: "https://images.unsplash.com/photo-1541701494587-cb58502866ab?w=600&h=340&fit=crop",
    duration: "12 min",
    level: "Intermédiaire",
    views: "8.2k",
  },
  {
    title: "Floop Pro : retouches professionnelles",
    description: "Découvrez comment utiliser Floop Pro pour des résultats de qualité studio.",
    thumbnail: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=340&fit=crop",
    duration: "15 min",
    level: "Intermédiaire",
    views: "6.8k",
  },
  {
    title: "Transformation Image vers Image",
    description: "Apprenez à transformer vos photos existantes avec le mode Image-to-Image.",
    thumbnail: "https://images.unsplash.com/photo-1633477189729-9290b3261d0a?w=600&h=340&fit=crop",
    duration: "10 min",
    level: "Débutant",
    views: "9.1k",
  },
  {
    title: "Créer des portraits réalistes",
    description: "Techniques avancées pour générer des portraits photoréalistes.",
    thumbnail: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=340&fit=crop",
    duration: "18 min",
    level: "Avancé",
    views: "5.4k",
  },
  {
    title: "Styles artistiques et effets",
    description: "Explorez les différents styles disponibles et comment les combiner.",
    thumbnail: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600&h=340&fit=crop",
    duration: "8 min",
    level: "Débutant",
    views: "7.3k",
  },
  {
    title: "Optimiser vos crédits",
    description: "Conseils pour maximiser vos crédits et obtenir plus de générations.",
    thumbnail: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&h=340&fit=crop",
    duration: "6 min",
    level: "Débutant",
    views: "11.2k",
  },
  {
    title: "Intégration API avancée",
    description: "Guide complet pour intégrer l'API Floop dans vos projets.",
    thumbnail: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&h=340&fit=crop",
    duration: "25 min",
    level: "Avancé",
    views: "3.2k",
  },
];

const getLevelColor = (level: string) => {
  switch (level) {
    case "Débutant":
      return "bg-success/20 text-success";
    case "Intermédiaire":
      return "bg-accent/20 text-accent";
    case "Avancé":
      return "bg-primary/20 text-primary";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const Tutorials = () => {
  const [selectedCategory, setSelectedCategory] = useState("Tous");

  const filteredTutorials = selectedCategory === "Tous"
    ? tutorials
    : tutorials.filter(t => t.level === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-12 text-center">
        <div className="container mx-auto px-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Tutoriels
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Apprenez à maîtriser Floop avec nos tutoriels vidéo et guides pas à pas.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="pb-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-gradient-floop text-white"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tutorials Grid */}
      <section className="pb-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTutorials.map((tutorial, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl overflow-hidden group cursor-pointer hover:border-primary/50 transition-colors"
              >
                <div className="relative aspect-video">
                  <img
                    src={tutorial.thumbnail}
                    alt={tutorial.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 rounded-full bg-gradient-floop flex items-center justify-center">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-background/80 rounded text-xs text-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {tutorial.duration}
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${getLevelColor(tutorial.level)}`}>
                      {tutorial.level}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      {tutorial.views} vues
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground mb-2 line-clamp-2">{tutorial.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{tutorial.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Vous avez une question ?
          </h2>
          <p className="text-muted-foreground mb-6">
            Consultez notre documentation ou contactez notre support.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/documentation" className="px-6 py-3 bg-gradient-floop text-white rounded-full font-medium hover:opacity-90 transition-opacity">
              Documentation
            </a>
            <a href="/help" className="px-6 py-3 bg-secondary text-foreground rounded-full font-medium hover:bg-secondary/80 transition-colors">
              Centre d'aide
            </a>
          </div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Tutorials;
